import { useEffect, useRef, useState } from "react";
import { Maximize, Maximize2, MessageSquare, Minimize2, X } from "lucide-react";
import { ScreenShareChatPanel } from "./ScreenShareChatPanel";

interface Props {
  stream: MediaStream | null;
  peerName: string | null;
  peerId: string | null;
  /** Both parties internal (have GG) — gates the 1:1 chat affordance. */
  canChat: boolean;
  onClose: () => void;
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const DEFAULT_W = 480;
const DEFAULT_H = 304;
const MIN_W = 280;
const MIN_H = 180;
const MARGIN = 16;
// Comfortable size when the side chat is shown (video + ~320px chat column).
const CHAT_MIN_W = 860;
const CHAT_MIN_H = 460;

const ICON_BTN =
  "rounded p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors disabled:opacity-40";

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max));

/** Default position: bottom-right dock. */
function defaultRect(): Rect {
  return {
    x: Math.max(MARGIN, window.innerWidth - DEFAULT_W - MARGIN),
    y: Math.max(MARGIN, window.innerHeight - DEFAULT_H - MARGIN),
    w: DEFAULT_W,
    h: DEFAULT_H,
  };
}

/**
 * Floating window showing the shared screen (viewer side). Freely draggable
 * (by the header) and resizable (bottom-right grip), plus maximize, native
 * fullscreen, and a 1:1 GG chat with the peer docked beside the video.
 */
export function ScreenShareViewerPanel({ stream, peerName, peerId, canChat, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<Rect>(defaultRect);
  const [maximized, setMaximized] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const preMaxRef = useRef<Rect | null>(null);

  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = stream;
  }, [stream]);

  // Generic pointer-drag driver shared by move + resize.
  const startDrag = (onMove: (dx: number, dy: number) => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    setInteracting(true);
    const move = (ev: MouseEvent) => onMove(ev.clientX - startX, ev.clientY - startY);
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      setInteracting(false);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const onHeaderMouseDown = (e: React.MouseEvent) => {
    if (maximized) return; // a maximized window doesn't drag
    if ((e.target as HTMLElement).closest("button")) return; // let header buttons click
    const orig = rect;
    startDrag((dx, dy) =>
      setRect((r) => ({
        ...r,
        x: clamp(orig.x + dx, 0, Math.max(0, window.innerWidth - orig.w)),
        y: clamp(orig.y + dy, 0, Math.max(0, window.innerHeight - orig.h)),
      })),
    )(e);
  };

  const onResizeMouseDown = (e: React.MouseEvent) => {
    if (maximized) return;
    const orig = rect;
    startDrag((dx, dy) =>
      setRect((r) => ({
        ...r,
        w: clamp(orig.w + dx, MIN_W, window.innerWidth - orig.x - MARGIN),
        h: clamp(orig.h + dy, MIN_H, window.innerHeight - orig.y - MARGIN),
      })),
    )(e);
  };

  const toggleMaximize = () => {
    if (maximized) {
      if (preMaxRef.current) setRect(preMaxRef.current);
      setMaximized(false);
    } else {
      preMaxRef.current = rect;
      setRect({
        x: MARGIN,
        y: MARGIN,
        w: window.innerWidth - 2 * MARGIN,
        h: window.innerHeight - 2 * MARGIN,
      });
      setMaximized(true);
    }
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else el.requestFullscreen().catch(() => {});
  };

  const toggleChat = () => {
    if (chatOpen) {
      setChatOpen(false);
      return;
    }
    setChatOpen(true);
    // Grow the window so video + chat both fit comfortably (unless maximized).
    if (!maximized) {
      setRect((r) => {
        const w = Math.min(window.innerWidth - 2 * MARGIN, Math.max(r.w, CHAT_MIN_W));
        const h = Math.min(window.innerHeight - 2 * MARGIN, Math.max(r.h, CHAT_MIN_H));
        return {
          w,
          h,
          x: clamp(r.x, 0, Math.max(0, window.innerWidth - w)),
          y: clamp(r.y, 0, Math.max(0, window.innerHeight - h)),
        };
      });
    }
  };

  return (
    <>
      {/* While dragging/resizing, an overlay keeps the cursor and stops the
          pointer being swallowed by elements under the panel. */}
      {interacting && <div className="fixed inset-0 z-[60] cursor-grabbing" />}
      <div
        ref={containerRef}
        style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
        className="fixed z-50 flex flex-col overflow-hidden rounded-xl border border-[var(--color-border)] bg-black shadow-2xl"
      >
        <div
          onMouseDown={onHeaderMouseDown}
          className={`flex shrink-0 select-none items-center justify-between bg-[var(--color-bg-surface)] px-3 py-1.5 ${
            maximized ? "" : "cursor-move"
          }`}
        >
          <span className="truncate text-xs font-medium text-[var(--color-text-secondary)]">
            Ekran: {peerName ?? "—"}
          </span>
          <div className="flex items-center gap-0.5">
            {canChat && (
              <button
                type="button"
                onClick={toggleChat}
                title="Czat 1:1"
                className={`${ICON_BTN} ${chatOpen ? "text-[var(--color-primary)]" : ""}`}
              >
                <MessageSquare size={14} />
              </button>
            )}
            <button
              type="button"
              onClick={toggleMaximize}
              title={maximized ? "Przywróć" : "Maksymalizuj"}
              className={ICON_BTN}
            >
              {maximized ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
            <button type="button" onClick={toggleFullscreen} title="Pełny ekran" className={ICON_BTN}>
              <Maximize size={14} />
            </button>
            <button
              type="button"
              onClick={onClose}
              title="Zakończ podgląd"
              className="rounded p-1 text-[var(--color-text-muted)] transition-colors hover:text-red-500"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="min-h-0 min-w-0 flex-1 bg-black object-contain"
          />
          {chatOpen && canChat && peerId && (
            <aside className="flex w-80 shrink-0 flex-col border-l border-[var(--color-border)] bg-[var(--color-bg-base)]">
              <ScreenShareChatPanel peerId={peerId} />
            </aside>
          )}
        </div>

        {/* Resize grip (bottom-right). */}
        {!maximized && (
          <div
            onMouseDown={onResizeMouseDown}
            title="Zmień rozmiar"
            className="absolute bottom-0 right-0 z-10 h-4 w-4 cursor-nwse-resize"
            style={{
              background:
                "linear-gradient(135deg, transparent 55%, var(--color-text-muted) 55%, var(--color-text-muted) 70%, transparent 70%)",
            }}
          />
        )}
      </div>
    </>
  );
}
